import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { MaterialPresetItem } from "../../../shared/api-contract";
import { createPreset, deletePreset } from "../../../features/presets/presetsApi";
import { confirmDialog } from "../../../shared/ui";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState, useEditorStore } from "../../../state/editor/useEditor";
import { downloadJSON, pickFile } from "../../../util/download";
import { Icon } from "../../kit";
import { CompactCheck, MiniBtn, Row } from "../inspector/rows";
import { useManifest, type ManifestEntry } from "./useManifest";
import { materialFileColor, materialFileUrl, materialFormatOf, usesDmat, type MaterialFormat } from "./materialRouting";
import { AddTile, PresetGrid, PresetTile } from "./PresetTile";
import { sphereFill } from "./sphereArt";

export type Category = MaterialPresetItem["category"];

export const CATEGORY_ORDER: Category[] = ["metal", "gem", "ceramic", "pearl", "other"];
export const CATEGORY_LABEL: Record<Category, string> = {
  metal: "Metal",
  gem: "Gem",
  ceramic: "Ceramic",
  pearl: "Pearl",
  other: "Other",
};
/** Panel titles — the reference pluralises them. */
export const CATEGORY_PANEL_LABEL: Record<Category, string> = {
  metal: "Metals",
  gem: "Gems",
  ceramic: "Ceramics",
  pearl: "Pearls",
  other: "Other",
};

const hexOf = (color: number) => "#" + color.toString(16).padStart(6, "0");

/**
 * iJewel's material library: 451 `.pmat`/`.dmat` files served from the
 * VIEWER's origin (their CDN sends no CORS header, so only that copy renders),
 * all five categories in one manifest — written by
 * scripts/ijewel/mirror-materials.mjs. Each category's hook reads its own
 * slice; `useManifest` fetches the file once for all of them.
 */
export const IJEWEL_MATERIALS = "/materials/ijewel/manifest.json";

/**
 * Chip and grid order for the library's groups.
 *
 * Plain precious metals first, then the FINISH families — hammered, brushed
 * and textured are what jewellers ask for by name, so they sit in the first
 * row of chips and are never folded behind "More". Groups not listed here
 * (Other's sub-categories, a combined grid's "Ceramics" / "Pearls") follow in
 * alphabetical order.
 */
const GROUP_ORDER = [
  // metal
  "Precious", "Hammered", "Brushed", "Textured", "Scratched", "Matte", "Sparkle", "Coloured",
  // gem
  "Diamonds", "Coloured stones", "With inclusions",
];
const groupRank = (g: string) => {
  const i = GROUP_ORDER.indexOf(g);
  return i < 0 ? GROUP_ORDER.length : i;
};
const byGroup = (a: string, b: string) => groupRank(a) - groupRank(b) || a.localeCompare(b);

/**
 * Number repeated names within a category. iJewel's library ships different
 * files under one name ("Emerald inclusions 01" twice, "Rose gold polished"
 * twice — their files differ by a "1-"/"2-" prefix we do not show), and two
 * identical captions side by side read as a bug. The first keeps its name.
 */
function distinctLabels(list: LibMaterial[]): LibMaterial[] {
  const seen = new Map<string, number>();
  return list.map((p) => {
    const key = `${p.category}\u0000${p.label.trim().toLowerCase()}`;
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    return n === 1 ? p : { ...p, label: `${p.label} (${n})` };
  });
}

/** Chips shown before "More (N)". Four keeps every metal finish family the
 *  user asked for in view; a fold of one chip is never worth its own button. */
const CHIPS_SHOWN = 4;

/**
 * The swatch's background.
 *
 * ⚠️ Returns exactly ONE property, and never the `background` shorthand
 * alongside `backgroundImage`. React walks a style object key by key, so a
 * shorthand set to `undefined` in the same object compiles to
 * `style.background = ""` — which resets every background longhand, including
 * the `backgroundImage` set a moment earlier. That is precisely what happened:
 * mirrored presets (which have a thumbnail) rendered with an EMPTY style
 * attribute, while built-ins (which fall to the colour branch) looked fine. It
 * stayed invisible until the catalogue import gave anything a thumbnail at all.
 *
 * A mirrored preset ships a rendered sphere, which is what the reference shows
 * and what actually distinguishes a brushed gold from a polished one.
 */
function swatchStyle(preset: LibMaterial, sphere = false): CSSProperties {
  if (preset.thumbUrl) return { backgroundImage: `url("${preset.thumbUrl}")` };
  // Basic's preset grid draws a lit sphere for what has no render (the
  // viewer's built-ins, a file someone added); the inspector keeps its chip.
  if (sphere) return { backgroundImage: sphereFill(preset) };
  if (preset.color != null) return { backgroundColor: hexOf(preset.color) };
  return { backgroundImage: "linear-gradient(135deg,#e6edf5,#c8d3e0)" };
}

/**
 * A curated entry may carry extra fields: a static manifest declares them, and
 * useManifest spreads an API preset's `values` into the item. `group` drives
 * the sub-category chips — the iJewel manifest files every metal, gem and
 * "other" material under one ("Hammered", "With inclusions", "Carbon"…). A
 * library with no groups simply renders no chips.
 */
type CuratedEntry = ManifestEntry & {
  group?: string;
  /** Swatch colour a workspace upload declared (see `addOwn`). */
  color?: number;
  /** A material file the workspace added from the studio: there is no CDN
   *  copy, the file's text lives in the preset itself. */
  materialFile?: { format?: MaterialFormat; json?: string };
};

/** A built-in preset (applied by id) or a library file (applied by URL). */
export type LibMaterial = MaterialPresetItem & {
  fileUrl?: string;
  thumbUrl?: string;
  group?: string;
  /** The workspace's own preset — the only kind a user may remove. */
  owned?: boolean;
  /** Known format of a file the workspace added. */
  format?: MaterialFormat;
};

/**
 * Where a library material goes when it is NOT the viewer selection — Basic
 * mode's panels, which pick their parts with chips and apply through the
 * Basic engine so the step is undoable. Without it the grid keeps the
 * inspector's behaviour: apply to the selected part, or nothing.
 */
export interface MaterialTarget {
  /** Apply to the host's chosen parts. The host owns the RPCs. */
  apply: (preset: LibMaterial) => Promise<unknown>;
  /** Is there anything to apply to? False disables the tiles. */
  hasTarget: boolean;
  /** The tile drawn as chosen. */
  activeId?: string | null;
  /** Said under the grid when `hasTarget` is false. */
  emptyText?: string;
  /** Offer the "+" tile (add a material file to the workspace library) and
   *  an × on the workspace's own presets. */
  custom?: boolean;
}

/** What the "+" tile accepts, per category. A ceramic or a pearl is a PBR
 *  surface (.pmat); "Other" carries both kinds, as the reference's does. */
const ADD_ACCEPT: Record<MaterialPresetItem["category"], string> = {
  metal: ".pmat",
  gem: ".dmat",
  ceramic: ".pmat",
  pearl: ".pmat",
  other: ".pmat,.dmat",
};

/** A material file lives inline in its preset document, and a preset is not a
 *  file store. A real .pmat is a few KB; what makes one large is textures
 *  embedded as base64, which the viewer does not apply anyway (it loads a
 *  material's textures from their URLs). */
const MAX_MATERIAL_BYTES = 2 * 1024 * 1024;

/** Saving to the workspace library needs a signed-in session with a workspace
 *  — the same two keys every API call sends. `/dev/studio` usually has neither. */
function canSaveToWorkspace(): boolean {
  try {
    return !!localStorage.getItem("auth_token") && !!localStorage.getItem("active_workspace_id");
  } catch {
    return false;
  }
}

/** "18K_rose-gold.pmat" → "18K rose gold". */
function nameFromFile(fileName: string): string {
  const base = fileName.replace(/\.(pmat|dmat|json)$/i, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return (base || "My material").slice(0, 120);
}

const errText = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Material operation failed.";

/** What is in flight, so the whole grid can lock while one apply runs (every
 *  job here rewrites the SAME selected group's material — two overlapping jobs
 *  would race and the loser's result would silently win the resync). */
type Job = { kind: "preset"; id: string } | { kind: "file"; id: string } | null;

/**
 * The applyable material swatch grid, shared by the right rail's Material
 * Library section and the five left-rail material panels.
 *
 * It is one component rather than two because everything interesting here is
 * the apply pipeline — the job interlock, the built-in-vs-curated routing, the
 * post-apply resync — and a second copy of that would drift. What the two
 * callers differ on is only chrome: tabs, the file row, the Enabled checkbox.
 *
 * Basic mode's rail panels are the third caller. They pass a `target`: the
 * apply goes to the parts the panel's chips chose (through the Basic engine,
 * as one Undo step) instead of the viewer selection, there is no selection
 * gate, and the grid draws as the reference's preset panel — round spheres,
 * names underneath, "+" to add your own.
 */
export function MaterialCategoryGrid({
  categories = CATEGORY_ORDER,
  showEnabled = false,
  showCategoryTabs = false,
  showFileIO = false,
  subGroups = true,
  columns = 3,
  target,
  hideIds,
  labels,
}: {
  /** Which categories this instance offers. */
  categories?: Category[];
  /** The section's local on/off switch (right rail only). */
  showEnabled?: boolean;
  /** Category tab strip — pointless when there is only one category. */
  showCategoryTabs?: boolean;
  /** The .pmat/.dmat import/export row. */
  showFileIO?: boolean;
  /** Sub-category chips derived from each preset's `group` — drawn only when
   *  the presets declare groups, so on by default. */
  subGroups?: boolean;
  columns?: number;
  /** Apply somewhere other than the viewer selection (Basic mode). */
  target?: MaterialTarget;
  /** Built-in presets to leave out of this grid. */
  hideIds?: string[];
  /** Display names for built-in presets, by id. */
  labels?: Record<string, string>;
}) {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();

  const selId = useDomainState("selection");
  const mat = useDomainState("material");

  const [presets, setPresets] = useState<MaterialPresetItem[]>([]);
  /** The viewer answered listMaterialPresets (so an empty grid means empty). */
  const [listed, setListed] = useState(false);
  const [tab, setTab] = useState<Category>(categories[0] ?? "metal");
  const [group, setGroup] = useState<string | null>(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [job, setJob] = useState<Job>(null);
  const [error, setError] = useState<string | null>(null);
  /** Per-category refetch keys, bumped after a preset is added or removed. */
  const [reload, setReload] = useState<Record<Category, number>>({ metal: 0, gem: 0, ceramic: 0, pearl: 0, other: 0 });
  const bump = (c: Category) => setReload((k) => ({ ...k, [c]: k[c] + 1 }));

  useEffect(() => {
    r.listMaterialPresets()
      .then((p) => { setPresets(p); setError(null); })
      // A failed listing used to render as "No material presets available.",
      // which reads as "the viewer has none" rather than "the call failed".
      .catch((e: unknown) => { setPresets([]); setError(errText(e)); })
      .finally(() => setListed(true));
  }, [r]);

  // ⚠️ One hook per category, ALWAYS all five, filtered afterwards. Mapping
  // over `categories` here would make the hook count depend on a prop.
  // Each merges iJewel's static library with the preset API's rows for that
  // kind; a database copy of an iJewel file is dropped in favour of the static
  // one, which loads from the viewer's own origin (see `mergeLibrary`).
  const curated: Record<Category, ReturnType<typeof useManifest<CuratedEntry>>> = {
    metal: useManifest<CuratedEntry>(IJEWEL_MATERIALS, "metal", reload.metal, { category: "metal" }),
    gem: useManifest<CuratedEntry>(IJEWEL_MATERIALS, "gem", reload.gem, { category: "gem" }),
    ceramic: useManifest<CuratedEntry>(IJEWEL_MATERIALS, "ceramic", reload.ceramic, { category: "ceramic" }),
    pearl: useManifest<CuratedEntry>(IJEWEL_MATERIALS, "pearl", reload.pearl, { category: "pearl" }),
    other: useManifest<CuratedEntry>(IJEWEL_MATERIALS, "other", reload.other, { category: "other" }),
  };
  const curatedItems = useMemo(
    () =>
      CATEGORY_ORDER.flatMap((c) =>
        curated[c].items.flatMap((i): LibMaterial[] => {
          // A material preset is applied BY ITS FILE: a CDN URL, or the text a
          // workspace upload carries inline. One with neither cannot be applied.
          const inline = i.materialFile;
          const fileUrl = i.file
            ? curated[c].urlFor(i)
            : inline?.json
              ? materialFileUrl(inline.json, inline.format === "dmat" ? "dmat" : "pmat", i.label)
              : null;
          if (!fileUrl) return [];
          return [{
            id: i.id,
            label: i.label,
            category: c,
            fileUrl,
            thumbUrl: i.thumb ? curated[c].thumbFor(i) : undefined,
            group: i.group,
            owned: i.owned === true,
            ...(inline ? { format: inline.format === "dmat" ? "dmat" as const : "pmat" as const } : {}),
            ...(typeof i.color === "number" ? { color: i.color } : {}),
          }];
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [curated.metal.items, curated.gem.items, curated.ceramic.items, curated.pearl.items, curated.other.items],
  );

  const mine = useMemo(
    () => new Set(categories),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories.join(",")],
  );
  const hidden = useMemo(
    () => new Set(hideIds ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(hideIds ?? []).join(",")],
  );
  /**
   * Built-ins first (the viewer's own presets, applied by id), then the
   * workspace's own materials — right after the "+" that added them, not
   * a hundred tiles down — then the library, family by family.
   *
   * A grid over two categories (Advanced's Metals = metal + ceramic) files the
   * second one under its own chip, so "Ceramics" is one click away instead of
   * forty tiles past the metals.
   */
  const primary = categories[0];
  const allPresets = useMemo<LibMaterial[]>(() => {
    const grouped = (p: LibMaterial): LibMaterial =>
      p.group || mine.size < 2 || p.category === primary ? p : { ...p, group: CATEGORY_PANEL_LABEL[p.category] };
    const builtIns = presets
      .filter((p) => !hidden.has(p.id))
      .map((p) => grouped(labels?.[p.id] ? { ...p, label: labels[p.id] } : p));
    const own = curatedItems.filter((p) => p.owned).map(grouped);
    const library = curatedItems
      .filter((p) => !p.owned)
      .map(grouped)
      .map((p, i) => ({ p, i }))
      .sort(
        (a, b) =>
          Number(!a.p.group) - Number(!b.p.group) ||
          (a.p.group && b.p.group ? byGroup(a.p.group, b.p.group) : 0) ||
          a.i - b.i,
      )
      .map(({ p }) => p);
    return distinctLabels([...builtIns, ...own, ...library].filter((p) => mine.has(p.category)));
  }, [presets, curatedItems, mine, primary, hidden, labels]);

  // Categories that actually have presets, in canonical order.
  const present = useMemo(() => {
    const has = new Set(allPresets.map((p) => p.category));
    return CATEGORY_ORDER.filter((c) => mine.has(c) && has.has(c));
  }, [allPresets, mine]);

  // Keep the active tab valid as presets load.
  useEffect(() => {
    if (present.length && !present.includes(tab)) setTab(present[0]);
  }, [present, tab]);

  const inTab = useMemo(
    () => (showCategoryTabs ? allPresets.filter((p) => p.category === tab) : allPresets),
    [allPresets, tab, showCategoryTabs],
  );

  // Sub-category chips, in GROUP_ORDER. `More (N)` folds the tail away; the
  // threshold is ours, not measured — the reference was only ever seen with
  // two chips plus a "More (10)", which does not pin down where it folds.
  const groupNames = useMemo(() => {
    if (!subGroups) return [];
    const seen = new Set<string>();
    for (const p of inTab) if (p.group) seen.add(p.group);
    return [...seen].sort(byGroup);
  }, [inTab, subGroups]);

  useEffect(() => {
    if (group && !groupNames.includes(group)) setGroup(null);
  }, [groupNames, group]);

  const visible = useMemo(
    () => (group ? inTab.filter((p) => p.group === group) : inTab),
    [inTab, group],
  );

  /**
   * Resync the inspector after the selection's material was replaced wholesale.
   * `material` carries a seed, so this genuinely re-reads the viewer — it must
   * be AWAITED, otherwise the busy state clears while the Material / Diamond
   * sections are still painting the pre-apply values.
   */
  const refreshAfterApply = () =>
    Promise.all([store.refresh("material"), store.refresh("groups")]);

  /**
   * One apply pipeline for presets and file imports: lock, run, resync, report.
   * A `target` host resyncs for itself (the Basic engine re-reads exactly the
   * parts it changed), so `resync` is off for its jobs.
   */
  const run = async (next: NonNullable<Job>, work: () => Promise<unknown>, resync = !target) => {
    setJob(next);
    setError(null);
    try {
      await work();
      if (resync) await refreshAfterApply();
    } catch (e) {
      setError(errText(e));
    } finally {
      setJob(null);
    }
  };

  const applyPreset = (preset: LibMaterial) => {
    if (target) {
      if (!target.hasTarget || job) return;
      return run({ kind: "preset", id: preset.id }, () => target.apply(preset));
    }
    if (!selId || !enabled) return;
    // A curated preset is a file on the CDN; a built-in is an id the viewer
    // already knows. Both go through the same job interlock.
    if (preset.fileUrl) {
      const url = preset.fileUrl;
      const dmat = usesDmat(url, preset.category);
      return run({ kind: "preset", id: preset.id }, () =>
        dmat ? r.applyDmatUrlToGroup(selId, url) : r.applyPmatUrlToGroup(selId, url));
    }
    return run({ kind: "preset", id: preset.id }, () => r.applyMaterialPresetToGroup(selId, preset.id));
  };

  const canApply = target ? target.hasTarget && job === null : !!selId && enabled && job === null;

  /**
   * "+": add a material file to the WORKSPACE library, then apply it.
   *
   * The file's text is stored inside the preset (`values.materialFile`) rather
   * than uploaded somewhere: a workspace's own storage may be a bucket the
   * viewer cannot fetch from, and the whole of a .pmat is a few KB. It is
   * handed to the viewer as a data: URL, through the same apply RPCs as a
   * curated file.
   */
  const addFile = async (category: Category, f: File) => {
    if (!target) return;
    if (f.size > MAX_MATERIAL_BYTES) {
      throw new Error(`That file is ${(f.size / 1048576).toFixed(1)} MB; a material file can be up to 2 MB.`);
    }
    const text = await f.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error(`“${f.name}” is not a material file.`);
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error(`“${f.name}” is not a material file.`);
    const format = materialFormatOf(f.name, parsed);
    const color = materialFileColor(parsed);
    const name = nameFromFile(f.name);
    const created = await createPreset({
      kind: category,
      name,
      values: { materialFile: { format, json: text }, ...(color != null ? { color } : {}) },
    });
    bump(category);
    await target.apply({ id: created._id, label: name, category, fileUrl: materialFileUrl(text, format, name), owned: true, format, color });
  };

  const addOwn = (category: Category) => {
    if (!target || job) return;
    setError(null);
    // The job starts INSIDE the callback: pickFile can resolve "cancelled" off
    // the window regaining focus while a picked file is still being read, and
    // a job that ended there would unlock the grid mid-upload.
    void pickFile(ADD_ACCEPT[category], (f) => run({ kind: "file", id: "add" }, () => addFile(category, f))).catch((e: unknown) =>
      setError(errText(e)),
    );
  };

  const removeOwn = async (preset: LibMaterial) => {
    if (job) return;
    const yes = await confirmDialog({
      title: `Remove “${preset.label}”?`,
      body: "It leaves this workspace's library for everyone in it. Parts already wearing it keep their look.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!yes) return;
    await run({ kind: "file", id: `remove:${preset.id}` }, async () => {
      await deletePreset(preset.id);
      bump(preset.category);
    }, false);
  };

  // exportMetalJSON/importMetalJSON are no-ops on a gem group and the diamond
  // pair are no-ops on a metal one (they return null / return early in the
  // viewer). Grey out the half that cannot apply instead of letting the click
  // do nothing with no explanation.
  const kind = mat?.kind ?? null;
  const metalOff = kind === "gem";
  const gemOff = kind === "metal";
  const fileBusy = (id: string) => job?.kind === "file" && job.id === id;
  const fileLocked = !selId || job !== null;

  /** Export helper — a null payload means the RPC refused the selection. */
  const exportJson = (id: string, get: () => Promise<string | null>, filename: string, why: string) =>
    run({ kind: "file", id }, async () => {
      const json = await get();
      if (!json) throw new Error(why);
      downloadJSON(json, filename);
    });

  const shownChips = groupsOpen || groupNames.length <= CHIPS_SHOWN + 1 ? groupNames : groupNames.slice(0, CHIPS_SHOWN);
  const hiddenChips = groupNames.length - shownChips.length;

  // Sub-category chips (only once presets declare groups).
  const chipRow =
    groupNames.length > 0 ? (
      <div className="flex flex-wrap items-center gap-1.5" data-material-groups>
        <button
          type="button"
          data-material-group=""
          aria-pressed={group === null}
          onClick={() => setGroup(null)}
          className={`rounded-full border px-2.5 py-0.5 text-[12px] transition-colors ${
            group === null
              ? "border-ed-accent bg-ed-active text-ed-text"
              : "border-ed-border bg-white text-ed-text hover:border-ed-accent"
          }`}
        >
          All
        </button>
        {shownChips.map((g) => (
          <button
            key={g}
            type="button"
            data-material-group={g}
            aria-pressed={group === g}
            onClick={() => setGroup(g === group ? null : g)}
            className={`rounded-full border px-2.5 py-0.5 text-[12px] transition-colors ${
              group === g
                ? "border-ed-accent bg-ed-active text-ed-text"
                : "border-ed-border bg-white text-ed-text hover:border-ed-accent"
            }`}
          >
            {g}
          </button>
        ))}
        {hiddenChips > 0 ? (
          <button
            type="button"
            onClick={() => setGroupsOpen(true)}
            className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[12px] text-ed-text-3 hover:text-ed-text"
          >
            More ({hiddenChips})
            <Icon name="expand_more" size={14} />
          </button>
        ) : null}
      </div>
    ) : null;

  if (target) {
    const addTo: Category = showCategoryTabs ? tab : categories[0] ?? "metal";
    const signedIn = canSaveToWorkspace();
    return (
      <div className="flex flex-col gap-3" data-material-grid={categories.join(",")}>
        {chipRow}
        <PresetGrid>
          {target.custom && signedIn ? (
            <AddTile
              label="Add yours"
              title={`Add your own material file (${ADD_ACCEPT[addTo]}) to this workspace`}
              busy={job?.kind === "file" && job.id === "add"}
              disabled={job !== null}
              onClick={() => addOwn(addTo)}
              data-material-add={addTo}
            />
          ) : null}
          {visible.map((preset) => {
            const builtIn = !preset.fileUrl;
            const data: Record<`data-${string}`, string> =
              builtIn && preset.category === "metal"
                ? { "data-metal-preset": preset.id }
                : builtIn && preset.category === "gem"
                  ? { "data-gem-preset": preset.id }
                  : { "data-material-preset": preset.id };
            return (
              <PresetTile
                key={preset.id}
                label={preset.label}
                preview={swatchStyle(preset, true)}
                on={target.activeId === preset.id}
                busy={job?.kind === "preset" && job.id === preset.id}
                disabled={!canApply}
                onClick={() => void applyPreset(preset)}
                onRemove={target.custom && preset.owned ? () => void removeOwn(preset) : undefined}
                {...data}
              />
            );
          })}
        </PresetGrid>
        {listed && visible.length === 0 && !error ? <span className="text-[12px] text-ed-text-2">Nothing here yet.</span> : null}
        {!target.hasTarget && target.emptyText ? <span className="text-[12px] leading-[17px] text-ed-text-2">{target.emptyText}</span> : null}
        {target.custom && !signedIn ? (
          <span className="text-[12px] leading-[17px] text-ed-text-2">Sign in to add your own materials to this library.</span>
        ) : null}
        {error ? <span className="text-[12px] leading-[17px] text-ed-red" role="alert">{error}</span> : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-material-grid={categories.join(",")}>
      {/* "Replace Material" deliberately is NOT rendered here: the section
          mounts ReplaceMaterialRow (picking.ts) as the next row, and two
          checkboxes over the same viewer flag — each with its own useState and
          its own mount-time seed — showed opposite values as soon as either was
          clicked (this copy also defaulted to true against a viewer default of
          false). The sibling row is the canonical one. */}
      {showEnabled ? (
        <Row label="Enabled"><CompactCheck checked={enabled} onChange={setEnabled} /></Row>
      ) : null}

      {showCategoryTabs ? (
        present.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {present.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setTab(c)}
                className={`rounded border px-2 py-0.5 text-[12px] font-medium transition-colors ${
                  tab === c
                    ? "border-ed-accent bg-ed-active text-ed-text"
                    : "border-ed-border bg-white text-ed-text hover:border-ed-accent"
                }`}
              >
                {CATEGORY_LABEL[c]}
              </button>
            ))}
          </div>
        ) : (
          <span className="text-[11px] text-ed-text-3">No material presets available.</span>
        )
      ) : null}

      {chipRow}

      {/* Swatch grid */}
      {visible.length > 0 ? (
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {visible.map((preset) => {
            const applying = job?.kind === "preset" && job.id === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                disabled={!canApply}
                aria-busy={applying || undefined}
                data-material-preset={preset.id}
                title={
                  !enabled
                    ? "Material Library is disabled"
                    : selId
                      ? `Apply ${preset.label}`
                      : "Select a part first"
                }
                onClick={() => void applyPreset(preset)}
                className={`flex flex-col items-stretch gap-1 rounded border bg-white p-1 text-left transition-colors ${
                  applying
                    ? "border-ed-accent"
                    : canApply
                      ? "border-ed-border hover:border-ed-accent"
                      : "cursor-not-allowed border-ed-border opacity-50"
                }`}
              >
                <span
                  className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-full border border-ed-border bg-cover bg-center"
                  style={swatchStyle(preset)}
                >
                  {/* applyMaterialPresetToGroup rebuilds the material (and, for
                      gem presets, bakes a cubemap) — without this the swatch
                      looked inert and invited a second click mid-apply. */}
                  {applying ? (
                    <Icon name="progress_activity" size={14} className="animate-spin text-ed-accent" />
                  ) : null}
                </span>
                <span className="truncate text-[11px] text-ed-text-2">{preset.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {!selId ? <span className="text-[11px] text-ed-text-3">Select a part first.</span> : null}
      {error ? <span className="text-[11px] text-ed-red">{error}</span> : null}

      {/* Legacy per-selection material JSON import/export */}
      {showFileIO ? (
        <div className="flex flex-col gap-2 border-t border-ed-border pt-2">
          <span className="text-[12px] font-semibold text-ed-text-2">Material file</span>
          <div className="flex flex-wrap gap-2">
            <MiniBtn
              busy={fileBusy("export-pmat")}
              disabled={fileLocked || metalOff}
              onClick={() => {
                if (!selId) return;
                void exportJson(
                  "export-pmat",
                  () => r.exportMetalJSON(selId),
                  "metal-material.pmat",
                  "This part has no metal material to export.",
                );
              }}
            >
              Export .pmat
            </MiniBtn>
            <MiniBtn
              busy={fileBusy("import-pmat")}
              disabled={fileLocked || metalOff}
              onClick={() => {
                if (!selId) return;
                // pickFile settles when the import + resync finish, so the spinner
                // covers the real work instead of just the dialog opening.
                void run({ kind: "file", id: "import-pmat" }, async () => {
                  let failed: unknown = null;
                  await pickFile(".pmat,.json", async (f) => {
                    // Caught inside onFile: pickFile chains `.finally` onto what we
                    // return, so a rejection escaping here is an unhandled rejection.
                    try {
                      await r.importMetalJSON(selId, await f.text());
                    } catch (e) {
                      failed = e;
                    }
                  });
                  if (failed) throw failed;
                });
              }}
            >
              Import .pmat
            </MiniBtn>
            <MiniBtn
              busy={fileBusy("export-dmat")}
              disabled={fileLocked || gemOff}
              onClick={() => {
                if (!selId) return;
                void exportJson(
                  "export-dmat",
                  () => r.exportDiamondJSON(selId),
                  "diamond-material.dmat",
                  "This part has no diamond material to export.",
                );
              }}
            >
              Export .dmat
            </MiniBtn>
            <MiniBtn
              busy={fileBusy("import-dmat")}
              disabled={fileLocked || gemOff}
              onClick={() => {
                if (!selId) return;
                void run({ kind: "file", id: "import-dmat" }, async () => {
                  let failed: unknown = null;
                  await pickFile(".dmat,.json", async (f) => {
                    try {
                      await r.importDiamondJSON(selId, await f.text());
                    } catch (e) {
                      failed = e;
                    }
                  });
                  if (failed) throw failed;
                });
              }}
            >
              Import .dmat
            </MiniBtn>
          </div>
          {/* Say WHY half the row is greyed out — the disabled MiniBtn has no
              tooltip, and the RPCs are silently kind-specific. */}
          {kind ? (
            <span className="text-[11px] text-ed-text-3">
              {kind === "gem"
                ? "This part is a gem — .pmat (metal) files do not apply."
                : "This part is metal — .dmat (diamond) files do not apply."}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
